import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { QrCode, Truck, MessageCircle } from "lucide-react";
import { api } from "@/lib/api";
import { ProductCard } from "@/components/shop/ProductCard";
import { Skeleton } from "@/components/ui/skeleton";

const useTabs = () => {
  const [tabs, setTabs] = useState([{ key: "", label: "Semua" }]);
  useEffect(() => {
    api.get("/categories").then((r) => setTabs([{ key: "", label: "Semua" }, ...r.data.filter((c) => !c.parent_id).map((c) => ({ key: c.id, label: c.name }))]));
  }, []);
  return tabs;
};

const Hero = () => (
  <section className="mx-auto grid max-w-7xl gap-6 px-4 pt-6 md:grid-cols-5 md:px-6 md:pt-10">
    <div className="flex flex-col justify-center rounded-xl border border-stone-200 bg-white p-6 md:col-span-2 md:p-10">
      <span className="w-fit rounded-full bg-amber-50 px-3 py-1 text-xs font-medium text-amber-800">Promo bulan ini · diskon hingga 15%</span>
      <h1 className="mt-4 text-4xl font-semibold leading-tight tracking-tight sm:text-5xl">Dirajut, dibentuk, dan dikemas dengan tangan.</h1>
      <p className="mt-4 text-base text-stone-600">Karya handmade siap pakai dan alat & bahan kerajinan untuk kamu yang suka berkarya. Dikirim dari Kosambi, Tangerang.</p>
      <a href="#katalog" data-testid="hero-shop-button" className="mt-6 w-fit rounded-lg bg-amber-700 px-5 py-3 text-sm font-medium text-white hover:bg-amber-800">Belanja Sekarang</a>
    </div>
    <div className="relative min-h-[260px] overflow-hidden rounded-xl md:col-span-3">
      <img src="/images/rings2.jpg" alt="Perlengkapan macrame dan benang" className="absolute inset-0 h-full w-full object-cover" />
    </div>
    <div className="grid gap-3 sm:grid-cols-3 md:col-span-5">
      {[[QrCode, "Bayar QRIS otomatis", "Terverifikasi instan tanpa upload bukti"], [Truck, "Ongkir otomatis", "Dihitung dari Kosambi, Tangerang"], [MessageCircle, "CS WhatsApp", "+62 858-1112-6712"]].map(([Icon, t, d]) => (
        <div key={t} className="flex items-center gap-3 rounded-lg border border-stone-200 bg-white p-4">
          <Icon className="h-5 w-5 text-amber-700" />
          <div><p className="text-sm font-medium">{t}</p><p className="text-xs text-stone-500">{d}</p></div>
        </div>
      ))}
    </div>
  </section>
);

export default function Home() {
  const [params, setParams] = useSearchParams();
  const category = params.get("kategori") || "";
  const q = params.get("q") || "";
  const [products, setProducts] = useState(null);
  const tabs = useTabs();

  useEffect(() => {
    setProducts(null);
    api.get("/products", { params: { category: category || undefined, q: q || undefined } }).then((r) => setProducts(r.data));
  }, [category, q]);

  const setCat = (k) => {
    const next = new URLSearchParams(params);
    if (k) next.set("kategori", k); else next.delete("kategori");
    setParams(next);
  };

  return (
    <div>
      <Hero />
      <section id="katalog" className="mx-auto max-w-7xl scroll-mt-28 px-4 pt-14 md:px-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <h2 className="text-2xl font-semibold">Katalog</h2>
            {q && <p className="mt-1 text-sm text-stone-600" data-testid="search-result-label">Hasil pencarian "{q}"</p>}
          </div>
          <div className="flex gap-2 overflow-x-auto" data-testid="category-tabs">
            {tabs.map((t) => (
              <button key={t.key} data-testid={`category-tab-${t.key || "all"}`} onClick={() => setCat(t.key)}
                className={`whitespace-nowrap rounded-full border px-4 py-2 text-sm ${category === t.key ? "border-amber-700 bg-amber-50 font-medium text-amber-900" : "border-stone-200 bg-white text-stone-600 hover:bg-stone-100"}`}>
                {t.label}
              </button>
            ))}
          </div>
        </div>
        <div className="mt-6 grid grid-cols-2 gap-3 md:grid-cols-3 md:gap-5 lg:grid-cols-4" data-testid="product-grid">
          {products === null && Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="aspect-[3/4] rounded-lg" />)}
          {products?.map((p) => <ProductCard key={p.id} p={p} />)}
        </div>
        {products?.length === 0 && (
          <p className="py-16 text-center text-sm text-stone-500" data-testid="product-empty">Produk tidak ditemukan. Coba kata kunci lain atau tanya CS kami via WhatsApp.</p>
        )}
      </section>
    </div>
  );
}
